import { ChangeDetectionStrategy, Component, computed, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Dialog } from '@angular/cdk/dialog';
import { TagEvent, TagPusherService } from '@libs/shared-services';
import { DeleteFfdNoteModalComponent, DeleteFfdNoteModalComponentResult } from '../delete-ffd-note-modal';
import { FfdNotesListComponent } from '../ffd-notes-list';
import { FfdNotesDetailComponent } from '../ffd-notes-detail';
import { FfdNotesScreenService } from './ffd-notes-screen.service';
import { FfdNoteItem } from './ffd-notes-screen.interface';

export const NUMBER_OF_LOADING_SKELETONS = 3;

@Component({
  selector: 'lib-ffd-notes-screen',
  imports: [CommonModule, FfdNotesListComponent, FfdNotesDetailComponent],
  templateUrl: './ffd-notes-screen.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './ffd-notes-screen.component.scss',
})
export class FfdNotesScreenComponent implements OnInit {
  protected readonly service = inject(FfdNotesScreenService);
  private readonly dialog = inject(Dialog);
  private readonly tagPusherService = inject(TagPusherService);

  protected readonly noteGroups = computed(() => this.service.viewModel().noteGroups);
  protected readonly searchTerm = computed(() => this.service.viewModel().searchTerm);
  protected readonly isLoading = computed(() => this.service.viewModel().loading);
  protected readonly infiniteScrollLoading = computed(() => this.service.viewModel().infiniteScrollLoading);
  protected readonly activeNoteGroupId = computed(() => this.service.viewModel().activeNoteGroupId);
  protected readonly selectedNoteId = computed(() => this.service.viewModel().selectedNoteId);
  protected readonly editedNoteId = computed(() => this.service.viewModel().editedNoteId);
  protected readonly activeNoteGroup = computed(() => {
    const groupId = this.activeNoteGroupId();
    if (!groupId) return null;
    return this.noteGroups().find((group) => group.visitId === groupId) ?? null;
  });
  protected readonly activeNote = computed(() => this.service.viewModel().selectedNote ?? null);
  protected readonly isNoteSaving = computed(() => this.service.viewModel().isNoteSaving);
  protected readonly isNoteSaved = computed(() => this.service.viewModel().isNoteSaved);

  protected readonly loadingSkeletons = computed(() => Array.from({ length: NUMBER_OF_LOADING_SKELETONS }).fill(0));

  public ngOnInit(): void {
    this.tagPusherService.pushTag({ event: TagEvent.FfdNotesHomeOpened });
    this.service.init();
  }

  protected onSearch(value: string | null): void {
    this.service.searchNotes(value);
  }

  protected onLoadMore(): void {
    this.service.loadMoreNotes();
  }

  protected onNoteClick(noteId: string): void {
    this.selectNote(noteId);
  }

  protected onNoteEditClick(noteId: string | null): void {
    if (noteId) {
      this.selectNote(noteId);
    }
    this.service.setEditedNoteId(noteId);
  }

  protected onNoteSave(saveData: FfdNoteItem): void {
    const originalNote = this.service
      .selectSignal((state) => state.expressNotes)()
      .find((note) => note.id === saveData.id);

    const hasContentChanged = originalNote?.content !== saveData.content;

    this.service.updateNote({ ...saveData, hasBeenCopied: hasContentChanged ? false : originalNote.hasBeenCopied });
  }

  protected onDeleteNote(noteId: string): void {
    const dialogRef = this.dialog.open<DeleteFfdNoteModalComponentResult>(DeleteFfdNoteModalComponent, {
      backdropClass: ['backdrop-blur-overlay', 'bg-overlay-light'],
    });

    dialogRef.closed.subscribe((result) => {
      if (result?.deleteFfdNote) {
        this.service.deleteNote(noteId);
      }
    });
  }

  private selectNote(noteId: string): void {
    const noteGroup = this.service.findNoteGroupByNoteId(this.noteGroups(), noteId);
    if (noteGroup) {
      this.service.setActiveNoteGroup(noteGroup.visitId);
      this.service.setSelectedNote(noteId);
      this.service.setEditedNoteId(null);
      this.tagPusherService.pushTag({ event: TagEvent.FfdNotesNoteDetailsOpened });
    }
  }
}
